import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
} from '@design-system';
import { Loader2 } from 'lucide-react';
import { UnifiedDiff } from './UnifiedDiff';

const MIN_NOTES_LENGTH = 10;

interface PublishDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  carrierName: string;
  draftContent: string;
  publishedContent: string;
  isPending: boolean;
  errorMessage?: string | null;
  onPublish: (notes: string) => void;
}

export function PublishDialog({
  open,
  onOpenChange,
  carrierName,
  draftContent,
  publishedContent,
  isPending,
  errorMessage,
  onPublish,
}: PublishDialogProps) {
  const [notes, setNotes] = useState('');
  const trimmed = notes.trim();
  const notesValid = trimmed.length >= MIN_NOTES_LENGTH;
  const sameContent = draftContent === publishedContent;

  return (
    <Dialog
      open={open}
      onOpenChange={next => {
        if (!isPending) {
          if (!next) setNotes('');
          onOpenChange(next);
        }
      }}
    >
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Publish {carrierName} guideline</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="mb-1 block text-sm font-medium">
              Changelog notes <span className="text-red-600">*</span>
            </Label>
            <textarea
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              rows={3}
              placeholder="What changed and why? (min 10 chars)"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              disabled={isPending}
            />
            {!notesValid && trimmed.length > 0 && (
              <div className="mt-1 text-xs text-red-600">
                Notes must be at least {MIN_NOTES_LENGTH} characters.
              </div>
            )}
          </div>
          <div>
            <Label className="mb-1 block text-sm font-medium">
              Diff vs currently published
            </Label>
            <UnifiedDiff before={publishedContent} after={draftContent} />
          </div>
          {errorMessage && (
            <div className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">
              {errorMessage}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={() => onPublish(trimmed)}
            disabled={!notesValid || isPending || sameContent}
          >
            {isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {isPending ? 'Publishing…' : 'Publish'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
